export type UiLanguage = 'en' | 'es';

export const ADMIN_CONSOLE_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    identityProvider: 'Identity Provider',
    identityRestricted: 'Only Keycloak group members can access Identity Provider administration.',
    accessManagement: 'Access Management',
    accessRestricted: 'Only NextCompli platform operators can access user, account, and role administration.',
    console: 'Console',
    breadcrumb: 'Breadcrumb',
    costOptimization: 'Cost Optimization',
    emailOperations: 'Email Operations',
    observability: 'Observability',
    experience: 'User experience',
    saving: 'Saving',
    deleting: 'Deleting',
    designAccess: 'Design secure access paths for your platform teams',
    sectionSplash: 'Overview',
    sectionRoles: 'Roles',
    sectionCrm: 'CRM',
    sectionUsers: 'Users',
    newUser: 'New user',
    sectionGroups: 'Accounts',
    sectionVoice: 'Voice Messages',
  },
  es: {
    identityProvider: 'Proveedor de identidad',
    identityRestricted: 'Solo los miembros del grupo Keycloak pueden administrar el proveedor de identidad.',
    accessManagement: 'Gestión de acceso',
    accessRestricted: 'Solo los operadores de la plataforma NextCompli pueden administrar usuarios, cuentas y roles.',
    console: 'Consola',
    breadcrumb: 'Ruta de navegación',
    costOptimization: 'Optimización de costos',
    emailOperations: 'Operaciones de correo',
    observability: 'Observabilidad',
    experience: 'Experiencia de usuario',
    saving: 'Guardando',
    deleting: 'Eliminando',
    designAccess: 'Diseña rutas de acceso seguras para los equipos de la plataforma',
    sectionSplash: 'Resumen',
    sectionRoles: 'Roles',
    sectionCrm: 'CRM',
    sectionUsers: 'Usuarios',
    newUser: 'Usuario nuevo',
    sectionGroups: 'Cuentas',
    sectionVoice: 'Mensajes de voz',
  },
};

export const THEME_DESIGNER_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    restricted: 'Website Theming Access Restricted',
    title: 'Website Theming',
    logo: 'Logo',
    themeLibrary: 'Theme Library',
    colorTheory: 'Color Theory Suggestions',
    colorPalette: 'Color Palette',
    typography: 'Typography & Style',
    restrictedBody: 'Only users with the Designer role can edit palette, typography, theme variables, and logo assets.',
  },
  es: {
    restricted: 'Acceso a tematización del sitio restringido',
    title: 'Tematización del sitio web',
    logo: 'Logotipo',
    themeLibrary: 'Biblioteca de temas',
    colorTheory: 'Sugerencias de teoría del color',
    colorPalette: 'Paleta de color',
    typography: 'Tipografía y estilo',
    restrictedBody: 'Solo los usuarios con el rol Designer pueden editar paleta, tipografía, variables de tema y logotipo.',
  },
};

export const MARKETING_STUDIO_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    title: 'Films',
    eyebrow: 'Razzmatazz · pre-production',
    lede: 'Script → beat sheet → animatic → assemble. Approve pacing here before spending on A-roll.',
  },
  es: {
    title: 'Películas',
    eyebrow: 'Razzmatazz · preproducción',
    lede: 'Guion → hoja de beats → animatic → ensamble. Aprueba el ritmo aquí antes de gastar en A-roll.',
  },
};

export const VOICE_MESSAGES_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    title: 'Voice Messages Management',
    loading: 'Loading voice message configuration...',
    save: 'Save Changes',
    saving: 'Saving...',
    loadingVoicemails: 'Loading voicemail records...',
    loginRequired: 'Please log in to manage voice messages.',
    subtitle: 'Edit automated greeting and AI bot prompt messages for your phone system.',
  },
  es: {
    title: 'Gestión de mensajes de voz',
    loading: 'Cargando configuración de mensajes de voz...',
    save: 'Guardar cambios',
    saving: 'Guardando...',
    loadingVoicemails: 'Cargando buzón de voz...',
    loginRequired: 'Inicia sesión para administrar los mensajes de voz.',
    subtitle: 'Edita el saludo automático y los mensajes del bot de IA para el sistema telefónico.',
  },
};

export const SUBSCRIPTIONS_DASHBOARD_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    loading: 'Loading subscriptions...',
    errorTitle: 'Error Loading Subscriptions',
    retry: 'Retry',
    title: 'Paid vendors',
    subtitle: 'Manage API credentials and passwords for services NextCompli pays for (AWS Secrets Manager).',
    billingLink: 'Customer billing →',
    empty: 'No subscriptions configured yet.',
    emptyHint: 'Add your first subscription to get started.',
    recentActivity: 'Recent Activity',
  },
  es: {
    loading: 'Cargando suscripciones...',
    errorTitle: 'Error al cargar suscripciones',
    retry: 'Reintentar',
    title: 'Proveedores de pago',
    subtitle: 'Administra credenciales y contraseñas de los servicios que paga NextCompli (AWS Secrets Manager).',
    billingLink: 'Facturación de clientes →',
    empty: 'Aún no hay suscripciones configuradas.',
    emptyHint: 'Añade la primera suscripción para comenzar.',
    recentActivity: 'Actividad reciente',
  },
};

export const PASSWORD_MANAGER_COPY: Record<UiLanguage, Record<string, string>> = {
  en: {
    noneSet: 'No password set for this subscription',
    add: 'Add Password',
    username: 'Username',
  },
  es: {
    noneSet: 'No hay contraseña para esta suscripción',
    add: 'Añadir contraseña',
    username: 'Usuario',
  },
};

export const CONSOLE_CHROME_TABLES = {
  admin: ADMIN_CONSOLE_COPY,
  themeDesigner: THEME_DESIGNER_COPY,
  marketingStudio: MARKETING_STUDIO_COPY,
  voiceMessages: VOICE_MESSAGES_COPY,
  subscriptions: SUBSCRIPTIONS_DASHBOARD_COPY,
  passwordManager: PASSWORD_MANAGER_COPY,
} as const;

export function storedUiLanguage(): UiLanguage {
  if (typeof window === 'undefined') {
    return 'en';
  }
  return window.localStorage.getItem('nextcompli.website.language') === 'es' ? 'es' : 'en';
}
